import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import HeroTerminal from "@/components/sections/HeroTerminal";

const MASK_LINES = [
    {
        parts: [
            { text: "Don't lead with " },
            { text: "emissions.", fade: true }
        ]
    },
    { text: "Lead with risk.", accent: true },
    { text: "Managing climate risk before it hits the balance sheet.", italic: true, small: true }
];

const revealVariant = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.13 }
    })
};

export default function Hero() {
    const ref = useRef(null);
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 800], [0, 180]);
    const imgY = useTransform(scrollY, [0, 800], [0, -60]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0.4]);
    const canvasRef = useRef(null);

    // subtle particle canvas simulating carbon molecules drifting
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let width = (canvas.width = canvas.offsetWidth * window.devicePixelRatio);
        let height = (canvas.height = canvas.offsetHeight * window.devicePixelRatio);
        const dpr = window.devicePixelRatio;

        const N = 60;
        const nodes = Array.from({ length: N }, () => ({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.12 * dpr,
            vy: (Math.random() - 0.5) * 0.12 * dpr,
            r: (Math.random() * 1.4 + 0.5) * dpr
        }));

        let raf;
        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            for (let i = 0; i < N; i++) {
                const n = nodes[i];
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > width) n.vx *= -1;
                if (n.y < 0 || n.y > height) n.vy *= -1;

                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(200, 155, 42, 0.55)";
                ctx.fill();

                for (let j = i + 1; j < N; j++) {
                    const m = nodes[j];
                    const dx = n.x - m.x;
                    const dy = n.y - m.y;
                    const d = Math.hypot(dx, dy);
                    if (d < 140 * dpr) {
                        ctx.strokeStyle = `rgba(46, 139, 133, ${(1 - d / (140 * dpr)) * 0.25})`;
                        ctx.lineWidth = 0.6 * dpr;
                        ctx.beginPath();
                        ctx.moveTo(n.x, n.y);
                        ctx.lineTo(m.x, m.y);
                        ctx.stroke();
                    }
                }
            }
            raf = requestAnimationFrame(draw);
        };
        draw();

        const onResize = () => {
            width = canvas.width = canvas.offsetWidth * dpr;
            height = canvas.height = canvas.offsetHeight * dpr;
        };
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <section
            ref={ref}
            className="relative min-h-[100svh] w-full overflow-hidden flex flex-col justify-center pb-16 pt-28 lg:pt-32"
            data-testid="hero-section"
        >
            {/* Parallax background image */}
            <motion.div
                style={{ y: imgY, opacity }}
                className="absolute inset-0 -z-10"
                aria-hidden="true"
            >
                <img
                    src={IMAGES.hero}
                    alt=""
                    className="w-full h-[120%] object-cover object-center opacity-15"
                    style={{ filter: "grayscale(60%) contrast(1) brightness(1.15)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/85 to-paper"></div>
                <div className="absolute inset-0 bg-grid opacity-70"></div>
            </motion.div>

            {/* Particle canvas overlay */}
            <canvas ref={canvasRef} className="absolute inset-0 -z-10 w-full h-full opacity-40" aria-hidden="true" />

            <motion.div style={{ y }} className="relative max-w-[1500px] mx-auto px-6 lg:px-10 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-7 flex flex-col gap-7 lg:gap-8">
                        <h1
                            className="font-serif leading-[0.95] tracking-[-0.03em] text-navy"
                            data-testid="hero-headline"
                        >
                            {MASK_LINES.map((line, i) => (
                                <span key={i} className="line-mask">
                                    <motion.span
                                        custom={i}
                                        initial="hidden"
                                        animate="show"
                                        variants={revealVariant}
                                        className={`block ${
                                            line.small
                                                ? "text-xl sm:text-2xl md:text-[26px] lg:text-[28px] mt-3 lg:mt-4"
                                                : "text-[44px] sm:text-[58px] md:text-[68px] lg:text-[72px] xl:text-[84px]"
                                        }`}
                                    >
                                        {line.parts ? (
                                            line.parts.map((part, pi) =>
                                                part.fade ? (
                                                    <span key={pi} className="hero-fade-word">
                                                        {part.text}
                                                    </span>
                                                ) : (
                                                    <span key={pi}>{part.text}</span>
                                                )
                                            )
                                        ) : line.italic ? (
                                            <em className="not-italic font-light text-navy/70 tracking-[-0.02em]">
                                                {line.text}
                                            </em>
                                        ) : line.accent ? (
                                            <em className="not-italic font-light text-gold">{line.text}</em>
                                        ) : (
                                            line.text
                                        )}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.15 }}
                            className="flex flex-col gap-7"
                        >
                            <p className="text-base lg:text-lg text-navy/70 leading-relaxed max-w-[560px]">
                                Carbonis helps MSMEs, banks, and regulators detect carbon exposure, quantify climate-linked financial risk, and implement mitigation pathways — before regulation, markets, or lenders force the issue.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <a href="https://platform.carbonis.in" target="_blank" rel="noopener noreferrer" className="btn-institution" data-testid="hero-cta-primary">
                                    Explore Platform <ArrowUpRight size={14} strokeWidth={1.5} />
                                </a>
                                <a href="#estimator" className="btn-ghost" data-testid="hero-cta-secondary">
                                    Risk Estimator <ArrowDown size={14} strokeWidth={1.5} />
                                </a>
                            </div>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5 lg:self-end">
                        <HeroTerminal />
                    </div>
                </div>

                {/* Positioning marker */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.35 }}
                    className="mt-10 lg:mt-12 pt-6 border-t border-navy/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                    data-testid="hero-launch-marker"
                >
                    <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-gold dot-live rounded-full"></span>
                        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-navy/60">
                            Climate Risk Intelligence · Live
                        </span>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/45">
                        For banks, regulators &amp; MSMEs in emerging markets · Hyderabad, India
                    </span>
                </motion.div>
            </motion.div>
        </section>
    );
}
